import React from "react";
import { FiMonitor, FiMoon, FiSun, FiType, FiZap } from "react-icons/fi";
import { useUiPreferences } from "../context/UiPreferencesContext";

const themeOptions = [
  { id: "light", label: "Light", Icon: FiSun },
  { id: "dark", label: "Dark", Icon: FiMoon },
  { id: "system", label: "Device", Icon: FiMonitor },
];

/**
 * Appearance & accessibility preferences (stored in localStorage, all roles).
 */
const PortalUiSettings = ({ className = "" }) => {
  const { prefs, setPrefs } = useUiPreferences();

  return (
    <section
      className={`rounded-2xl border border-slate-200/80 dark:border-slate-600/80 p-5 sm:p-6 bg-white dark:bg-slate-900/70 shadow-sm ${className}`}
      aria-labelledby="portal-ui-settings-heading"
    >
      <h3
        id="portal-ui-settings-heading"
        className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-1"
      >
        Portal appearance
      </h3>
      <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
        Theme, text size, and motion apply across student, faculty, and admin
        dashboards on this browser.
      </p>

      <div className="space-y-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
            Theme
          </p>
          <div className="flex flex-wrap gap-2">
            {themeOptions.map(({ id, label, Icon }) => {
              const active = prefs.theme === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setPrefs({ theme: id })}
                  className={`
                    inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold border transition-all
                    ${
                      active
                        ? "bg-indigo-600 text-white border-indigo-500 shadow-md shadow-indigo-500/25"
                        : "bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-600 hover:border-indigo-300 dark:hover:border-indigo-500"
                    }
                  `}
                >
                  <Icon className="w-4 h-4 shrink-0" aria-hidden />
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2 flex items-center gap-2">
            <FiType className="w-3.5 h-3.5" aria-hidden />
            Text size
          </p>
          <div className="flex flex-wrap gap-2">
            {[
              { id: "normal", label: "Standard" },
              { id: "large", label: "Larger" },
            ].map(({ id, label }) => {
              const active = prefs.fontScale === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setPrefs({ fontScale: id })}
                  className={`
                    rounded-xl px-4 py-2.5 text-sm font-semibold border transition-all
                    ${
                      active
                        ? "bg-violet-600 text-white border-violet-500"
                        : "bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-600"
                    }
                  `}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50/80 dark:bg-slate-800/50 p-4">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            checked={prefs.reduceMotion}
            onChange={(e) => setPrefs({ reduceMotion: e.target.checked })}
          />
          <span>
            <span className="flex items-center gap-2 font-semibold text-slate-900 dark:text-slate-100">
              <FiZap className="w-4 h-4 text-amber-500" aria-hidden />
              Reduce interface motion
            </span>
            <span className="block text-sm text-slate-600 dark:text-slate-400 mt-0.5">
              Shortens animations for a calmer experience (also respects your
              system “reduce motion” when combined with our toggle).
            </span>
          </span>
        </label>
      </div>
    </section>
  );
};

export default PortalUiSettings;
